// Central SEO utility — sets document.title, description, canonical, Open Graph
// tags, and injects structured data (JSON-LD) for a given page.

import { COMPANY } from "@/data/site";

const SITE_URL = "https://peakplumbingmi.com";
const OG_IMAGE = COMPANY.logo;

function ensureTag(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaByName(name, content) {
  const el = ensureTag(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  const el = ensureTag(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  });
  el.setAttribute("content", content);
}

function setCanonical(url) {
  const el = ensureTag('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  });
  el.setAttribute("href", url);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function clearJsonLd(id) {
  const el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (el) el.remove();
}

/**
 * setSeo — call once per route inside useEffect.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} opts.path — e.g. "/plumbing/troy"
 * @param {object[]} [opts.jsonLd] — one or more structured data objects; each
 *   entry must be `{ id: string, data: object }`.
 * @param {string} [opts.image] — OG/Twitter image URL
 * @param {string} [opts.type] — OG type; default "website"
 */
export function setSeo({ title, description, path, jsonLd = [], image = OG_IMAGE, type = "website" }) {
  const url = `${SITE_URL}${path}`;
  document.title = title;
  setMetaByName("description", description);
  setCanonical(url);
  // Open Graph
  setMetaByProperty("og:title", title);
  setMetaByProperty("og:description", description);
  setMetaByProperty("og:type", type);
  setMetaByProperty("og:url", url);
  setMetaByProperty("og:image", image);
  setMetaByProperty("og:site_name", COMPANY.name);
  // Twitter
  setMetaByName("twitter:card", "summary_large_image");
  setMetaByName("twitter:title", title);
  setMetaByName("twitter:description", description);
  setMetaByName("twitter:image", image);
  // JSON-LD (structured data)
  const currentIds = new Set(jsonLd.map((j) => j.id));
  // Remove stale scripts from prior route
  document.head.querySelectorAll("script[data-seo-id]").forEach((el) => {
    const id = el.getAttribute("data-seo-id");
    if (!currentIds.has(id)) el.remove();
  });
  jsonLd.forEach(({ id, data }) => setJsonLd(id, data));
}

// ---------------- Structured-data builders ---------------------------------

export function buildLocalBusinessSchema({ areaServed } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Plumber",
    "@id": `${SITE_URL}#organization`,
    name: COMPANY.name,
    image: COMPANY.logo,
    logo: COMPANY.logo,
    url: SITE_URL,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    priceRange: "$$",
    slogan: COMPANY.tagline,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Roseville",
      addressRegion: "MI",
      postalCode: "48066",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 42.4972,
      longitude: -82.9371,
    },
    areaServed: (areaServed && areaServed.length
      ? areaServed
      : ["Macomb County", "Oakland County"]
    ).map((n) => ({ "@type": "AdministrativeArea", name: n })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(COMPANY.rating),
      reviewCount: String(COMPANY.reviewCount),
    },
    sameAs: [],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Plumbing Services",
      itemListElement: [
        "Drain Cleaning",
        "Sewer Line Repair",
        "Hydro Jetting",
        "Water Heater Service",
        "Leak Detection",
        "Sump Pumps",
        "Sewer Camera Inspection",
        "Emergency Plumbing",
      ].map((s, i) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s },
        position: i + 1,
      })),
    },
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildServiceSchema({ serviceName, cityName }) {
  const inCity = cityName ? ` in ${cityName}, MI` : "";
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: serviceName,
    name: `${serviceName}${inCity}`,
    provider: { "@id": `${SITE_URL}#organization` },
    areaServed: cityName
      ? { "@type": "City", name: `${cityName}, MI` }
      : { "@type": "AdministrativeArea", name: "Macomb & Oakland Counties, MI" },
    offers: { "@type": "Offer", availability: "https://schema.org/InStock" },
  };
}

export { SITE_URL };
